import { ImpersonationModal } from '@/components/sysadmin/ImpersonationModal';
import { Badge, Button, Card, Input, LoadingContainer, Modal, Select } from '@/components/ui';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useAsesores, useAsesorFormUrls, useDriveSysadmin } from '@/hooks/useSysadmin';
import type { AsesorFormUrlsResponse, AsesorSysadmin, CrearAsesorRequest } from '@/services/sysadmin.service';
import {
  AlertCircle,
  CheckCircle,
  Copy,
  Edit2,
  ExternalLink,
  FileText,
  MoreVertical,
  Power,
  Save,
  Search,
  Shield,
  UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

function getRolBadge(rol: string) {
  switch (rol) {
    case 'SYSADMIN':
      return <Badge variant="red">SYSADMIN</Badge>;
    case 'ADMIN':
      return <Badge variant="blue">ADMIN</Badge>;
    case 'ASESOR':
      return <Badge variant="green">ASESOR</Badge>;
    default:
      return <Badge variant="gray">{rol}</Badge>;
  }
}

interface AsesorFormData {
  nombre: string;
  correo: string;
  telefono: string;
  rol: 'ADMIN' | 'ASESOR' | 'SYSADMIN';
}

const emptyForm: AsesorFormData = { nombre: '', correo: '', telefono: '', rol: 'ASESOR' };

export function GestionUsuariosPage() {
  const { asesores, isLoading, error, crearAsesor, actualizarAsesor, toggleEstado } = useAsesores();
  const { getFormUrls } = useAsesorFormUrls();
  const { copyFileAndShare, isLoading: isDuplicating } = useDriveSysadmin();
  const { canImpersonate, impersonate } = useImpersonation();

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [showUrlsModal, setShowUrlsModal] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<AsesorFormUrlsResponse | null>(null);
  const [selectedAsesor, setSelectedAsesor] = useState<AsesorSysadmin | null>(null);
  const [editingAsesor, setEditingAsesor] = useState<AsesorSysadmin | null>(null);
  const [form, setForm] = useState<AsesorFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // URL form fields (editable)
  const [viewUrlInput, setViewUrlInput] = useState('');
  const [editUrlInput, setEditUrlInput] = useState('');
  const [savingUrls, setSavingUrls] = useState(false);
  const [urlsSaved, setUrlsSaved] = useState(false);

  // Drive copy fields
  const [destinationFolderId, setDestinationFolderId] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [emailsInput, setEmailsInput] = useState('');
  const [copyResult, setCopyResult] = useState<{ url: string; id: string } | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const filteredAsesores = useMemo(() => {
    if (!search) return asesores;
    const q = search.toLowerCase();
    return asesores.filter((a) => a.nombre.toLowerCase().includes(q) || a.correo.toLowerCase().includes(q));
  }, [asesores, search]);

  const openCrear = () => {
    setEditingAsesor(null);
    setForm(emptyForm);
    setActionError(null);
    setShowModal(true);
  };

  const openEditar = (asesor: AsesorSysadmin) => {
    setEditingAsesor(asesor);
    setForm({ nombre: asesor.nombre, correo: asesor.correo, telefono: asesor.telefono, rol: asesor.rol });
    setActionError(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.nombre || !form.correo || !form.telefono) {
      setActionError('Todos los campos son obligatorios');
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      if (editingAsesor) {
        await actualizarAsesor(editingAsesor.asesorId, form);
      } else {
        await crearAsesor(form as CrearAsesorRequest);
      }
      setShowModal(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (asesorId: number) => {
    try {
      await toggleEstado(asesorId);
    } catch (err) {
      console.error('Error al cambiar estado:', err);
    }
  };

  const handleShowUrls = async (asesor: AsesorSysadmin) => {
    setOpenMenuId(null);
    setSelectedAsesor(asesor);
    setSelectedUrls(null);
    setViewUrlInput('');
    setEditUrlInput('');
    setDestinationFolderId('');
    setNewFileName(`${asesor.nombre} - Formulario de Audio`);
    setEmailsInput(asesor.correo);
    setCopyResult(null);
    setCopyError(null);
    setUrlsSaved(false);
    setShowUrlsModal(true);

    try {
      const res = await getFormUrls(asesor.asesorId);
      setSelectedUrls(res);
      setViewUrlInput(res.viewUrl ?? '');
      setEditUrlInput(res.editUrl ?? '');
    } catch (err) {
      console.error('Error al obtener URLs:', err);
    }
  };

  const handleSaveUrls = async () => {
    if (!selectedAsesor) return;
    setSavingUrls(true);
    setUrlsSaved(false);
    try {
      const parts = [viewUrlInput.trim(), editUrlInput.trim()].filter(Boolean);
      const raw = parts.join(' ');
      await actualizarAsesor(selectedAsesor.asesorId, { urlFormularioAudio: raw || null });
      setUrlsSaved(true);
      setTimeout(() => setUrlsSaved(false), 3000);
    } catch (err) {
      console.error('Error guardando URLs:', err);
    } finally {
      setSavingUrls(false);
    }
  };

  const DEFAULT_DESTINATION_FOLDER_ID = '1tQANCgKYOnb_ux4yYsEBqnKs4bOj2t11';
  const DEFAULT_SOURCE_FILE_ID = '18l49gBEghO7WdYWdGIXSm7KYy82tQaQV5tGZPwLkGX8';

  const handleCopyDriveFile = async () => {
    if (!newFileName.trim()) {
      setCopyError('El nombre del archivo es obligatorio.');
      return;
    }
    const emailsList = emailsInput
      .split(/[\n,]/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emailsList.length === 0) {
      setCopyError('Debes ingresar al menos un correo válido.');
      return;
    }
    setCopyError(null);
    setCopyResult(null);
    const finalFolderId = destinationFolderId.trim() || DEFAULT_DESTINATION_FOLDER_ID;
    try {
      const res = await copyFileAndShare({
        sourceFileId: DEFAULT_SOURCE_FILE_ID,
        destinationFolderId: finalFolderId,
        newFileName: newFileName.trim(),
        emails: emailsList,
        role: 'writer',
        delayMs: 150,
      });
      setCopyResult({ url: res.file.url, id: res.file.id });
      // Pre-fill the edit URL field with the new form's edit link
      setEditUrlInput(`https://docs.google.com/forms/d/${res.file.id}/edit`);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : 'Error al duplicar el archivo en Drive.');
    }
  };

  const handleImpersonate = async (asesor: AsesorSysadmin) => {
    setOpenMenuId(null);
    const ok = window.confirm(`¿Suplantar a ${asesor.nombre}? Verás la plataforma como si fueras él.`);
    if (!ok) return;

    try {
      await impersonate(asesor.asesorId);
      window.location.replace('/');
    } catch (err) {
      console.error('Error al iniciar impersonación:', err);
      alert('Error al iniciar impersonación');
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando usuarios..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  return (
    <div className="space-y-4">
      {/* Barra superior */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar por nombre o correo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex gap-2">
          {canImpersonate && (
            <Button
              variant="outline"
              onClick={() => setShowImpersonationModal(true)}
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
            >
              <Shield className="w-4 h-4 mr-2" />
              Suplantar Asesor
            </Button>
          )}
          <Button onClick={openCrear}>
            <UserPlus className="w-4 h-4 mr-2" />
            Nuevo usuario
          </Button>
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '700px' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Correo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rol</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Empresas</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAsesores.map((asesor) => (
                <tr key={asesor.asesorId} className={`hover:bg-gray-50 ${!asesor.estado ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{asesor.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">{asesor.correo}</td>
                  <td className="px-4 py-3 text-gray-600">{asesor.telefono}</td>
                  <td className="px-4 py-3 text-center">{getRolBadge(asesor.rol)}</td>
                  <td className="px-4 py-3 text-center text-gray-600">{asesor.cantidadEmpresas}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={asesor.estado ? 'green' : 'gray'}>{asesor.estado ? 'Activo' : 'Inactivo'}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <div className="relative" ref={openMenuId === asesor.asesorId ? menuRef : null}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            if (openMenuId === asesor.asesorId) {
                              setOpenMenuId(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const estimatedHeight = asesor.rol === 'ASESOR' ? 170 : 85;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const showAbove = spaceBelow < estimatedHeight + 10;

                              setMenuPosition({
                                top: showAbove ? rect.top - estimatedHeight - 4 : rect.bottom + 4,
                                left: rect.right - 192,
                              });
                              setOpenMenuId(asesor.asesorId);
                            }
                          }}
                          title="Acciones"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>

                        {openMenuId === asesor.asesorId && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                            <div
                              className="fixed w-52 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1 text-left"
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                            >
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  openEditar(asesor);
                                }}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Edit2 className="w-4 h-4 text-gray-500" />
                                Editar usuario
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleToggle(asesor.asesorId);
                                }}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Power className={`w-4 h-4 ${asesor.estado ? 'text-red-500' : 'text-green-500'}`} />
                                {asesor.estado ? 'Desactivar' : 'Activar'}
                              </button>

                              {asesor.rol === 'ASESOR' && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  <button
                                    onClick={() => handleShowUrls(asesor)}
                                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4 text-blue-500" />
                                    URLs del Formulario
                                  </button>

                                  {canImpersonate && asesor.estado && (
                                    <button
                                      onClick={() => handleImpersonate(asesor)}
                                      className="w-full px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2 font-medium"
                                    >
                                      <Shield className="w-4 h-4" />
                                      Acceder como...
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal crear/editar */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingAsesor ? 'Editar usuario' : 'Crear usuario'}>
        <div className="space-y-4">
          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre completo" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
            <Input
              type="email"
              value={form.correo}
              onChange={(e) => setForm({ ...form, correo: e.target.value })}
              placeholder="correo@ejemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="3001234567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <Select
              value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value as AsesorFormData['rol'] })}
              options={[
                { value: 'ASESOR', label: 'Asesor' },
                { value: 'ADMIN', label: 'Administrador' },
                { value: 'SYSADMIN', label: 'Super Admin' },
              ]}
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Guardando...' : editingAsesor ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal impersonación */}
      <ImpersonationModal open={showImpersonationModal} onClose={() => setShowImpersonationModal(false)} />

      {/* Modal URLs Formulario de Audio */}
      <Modal
        isOpen={showUrlsModal}
        onClose={() => setShowUrlsModal(false)}
        title={`URLs del Formulario de Audio${selectedAsesor ? ` — ${selectedAsesor.nombre}` : ''}`}
      >
        <div className="space-y-5">
          {/* ── Sección: Configurar URLs ── */}
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">URL de Vista (Publicada)</label>
              <div className="flex gap-2">
                <Input
                  value={viewUrlInput}
                  onChange={(e) => setViewUrlInput(e.target.value)}
                  placeholder="https://docs.google.com/forms/d/e/..."
                  className="flex-1"
                  disabled={!selectedUrls}
                />
                {viewUrlInput && (
                  <Button variant="ghost" size="sm" onClick={() => window.open(viewUrlInput, '_blank')} title="Abrir URL">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase">URL de Edición</label>
              <div className="flex gap-2">
                <Input
                  value={editUrlInput}
                  onChange={(e) => setEditUrlInput(e.target.value)}
                  placeholder="https://docs.google.com/forms/d/.../edit"
                  className="flex-1"
                  disabled={!selectedUrls}
                />
                {editUrlInput && (
                  <Button variant="ghost" size="sm" onClick={() => window.open(editUrlInput, '_blank')} title="Abrir URL">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={handleSaveUrls} disabled={savingUrls || isDuplicating || !selectedUrls} size="sm">
                <Save className="w-4 h-4 mr-1.5" />
                {savingUrls ? 'Guardando...' : 'Guardar URLs'}
              </Button>
              {urlsSaved && (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" /> URLs guardadas
                </span>
              )}
            </div>
          </div>

          {/* ── Separador ── */}
          <div className="border-t border-gray-200" />

          {/* ── Sección: Copiar plantilla desde Drive ── */}
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Copy className="w-4 h-4 text-blue-500" />
                Copiar formulario desde plantilla
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Duplica la plantilla base de formulario de audio en una carpeta de Drive y comparte el acceso de edición al correo indicado.
                La URL de edición se completará automáticamente.
              </p>
            </div>

            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  ID Carpeta Destino <span className="text-red-500">*</span>
                </label>
                <Input
                  value={destinationFolderId}
                  onChange={(e) => setDestinationFolderId(e.target.value)}
                  placeholder="1tQANCgKYOnb_ux4yYsEBqnKs4bOj2t11"
                  disabled={isDuplicating}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Nombre del nuevo archivo <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  placeholder="Ej. EMPRESA - Formulario de Audio"
                  disabled={isDuplicating}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Correos para compartir (rol Editor) <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={2}
                  value={emailsInput}
                  onChange={(e) => setEmailsInput(e.target.value)}
                  placeholder="correo@ejemplo.com, otro@ejemplo.com"
                  disabled={isDuplicating}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none disabled:opacity-50"
                />
                <p className="text-xs text-gray-400 mt-0.5">Separa los correos con comas o saltos de línea.</p>
              </div>
            </div>

            {copyError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-start gap-2 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {copyError}
              </div>
            )}

            {copyResult && (
              <div className="p-3 bg-green-50 text-green-700 rounded-lg flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Archivo duplicado correctamente.</p>
                  <p className="text-xs mt-0.5">ID: {copyResult.id}</p>
                  <a href={copyResult.url} target="_blank" rel="noreferrer" className="text-green-600 underline text-xs">
                    Abrir en Drive
                  </a>
                  <p className="text-xs text-green-600 mt-1">
                    La URL de edición fue completada automáticamente. Guarda las URLs para aplicar el cambio.
                  </p>
                </div>
              </div>
            )}

            <Button onClick={handleCopyDriveFile} disabled={isDuplicating || savingUrls} variant="secondary" size="sm">
              <Copy className="w-4 h-4 mr-1.5" />
              {isDuplicating ? 'Duplicando...' : 'Duplicar y compartir formulario'}
            </Button>
          </div>

          <div className="flex justify-end pt-1 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowUrlsModal(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
